import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { ibkrService } from '../../services/ibkrService';

interface Snapshot {
  timestamp: string;
  nlv: number;
  unrealized_pnl: number;
  realized_pnl: number;
}

interface DepotReport {
  start_date: string;
  start_nlv: number;
  current_nlv: number;
  change_abs: number;
  change_pct: number;
  high: number;
  low: number;
  snapshots: Snapshot[];
  total_snapshots: number;
}

const IBKRDepotLog: React.FC = () => {
  const [report, setReport] = useState<DepotReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [saving, setSaving] = useState(false);

  const formatCurrency = (val: number) =>
    `$${val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fetchReport = useCallback(async () => {
    try {
      setError(null);
      const data = await ibkrService.getDepotLog();
      setReport(data);
      if (data.start_date) {
        setStartDate(data.start_date.slice(0, 10));
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden des Depot-Logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
    const interval = setInterval(fetchReport, 60000);
    return () => clearInterval(interval);
  }, [fetchReport]);

  const handleSetStart = async () => {
    if (!startDate) return;
    setSaving(true);
    try {
      await ibkrService.setDepotStart(startDate);
      await fetchReport();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Depot-Log wirklich loeschen? Alle aufgezeichneten Werte gehen verloren.')) return;
    try {
      await ibkrService.resetDepotLog();
      await fetchReport();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const chartData = (report?.snapshots || []).map((s) => ({
    time: new Date(s.timestamp).getTime(),
    nlv: s.nlv,
    label: new Date(s.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
  }));

  const isPositive = (report?.change_abs ?? 0) >= 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Depot-Entwicklung
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Startzeitpunkt setzen */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Startzeitpunkt"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSetStart}
          disabled={saving || !startDate}
        >
          {saving ? 'Speichere...' : 'Startzeitpunkt setzen'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={handleReset}
        >
          Log zuruecksetzen
        </Button>
        {report && report.total_snapshots > 0 && (
          <Typography variant="body2" color="text.secondary">
            {report.total_snapshots} Datenpunkte aufgezeichnet
          </Typography>
        )}
      </Paper>

      {/* Uebersicht */}
      {report && report.start_nlv > 0 ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 2 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Startwert</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(report.start_nlv)}</Typography>
              <Typography variant="caption" color="text.secondary">
                am {new Date(report.start_date).toLocaleDateString('de-DE')}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Aktueller Wert</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(report.current_nlv)}</Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Veraenderung</Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                color={isPositive ? 'success.main' : 'error.main'}
              >
                {isPositive ? '+' : ''}{formatCurrency(report.change_abs)}
              </Typography>
              <Chip
                label={`${isPositive ? '+' : ''}${report.change_pct.toFixed(2)}%`}
                color={isPositive ? 'success' : 'error'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Hoechstwert</Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {formatCurrency(report.high)}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Tiefstwert</Typography>
              <Typography variant="h6" fontWeight={700} color="error.main">
                {formatCurrency(report.low)}
              </Typography>
            </Paper>
          </Box>

          {/* Equity-Kurve */}
          {chartData.length > 1 && (
            <Paper sx={{ p: 2 }}>
              <Typography fontWeight={600} gutterBottom>
                Equity-Kurve
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="time"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={(t) => new Date(t).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                    fontSize={12}
                  />
                  <YAxis
                    domain={['auto', 'auto']}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    fontSize={12}
                  />
                  <Tooltip
                    labelFormatter={(t) => new Date(t as number).toLocaleString('de-DE')}
                    formatter={(value: number) => [formatCurrency(value), 'NLV']}
                  />
                  <ReferenceLine
                    y={report.start_nlv}
                    stroke="#999"
                    strokeDasharray="5 5"
                    label={{ value: 'Start', position: 'right', fontSize: 11 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="nlv"
                    stroke={isPositive ? '#22c55e' : '#ef4444'}
                    strokeWidth={2}
                    dot={false}
                    name="NLV"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Noch keine Daten vorhanden. Starte Auto-Trading — der Depotwert wird bei jeder Analyse automatisch aufgezeichnet.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default IBKRDepotLog;
